// ContributionsCard — Process 1. Current rates + 2026 IRS-limit dial + a live
// "adjust your rate" simulator (pre-tax / Roth via the reusable RateControl)
// with validate-on-change and an Apply action. Built on the reusable ProcessCard
// shell (desktop chrome ≥601px, phone accordion ≤600px). Presentational: the
// simulator state is local UI only; Apply hands off via onAction.
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import { formatCurrency } from '../../../utils/format';
import { rateRows } from '../../../utils/contributions';
import { ProcessCard, RateControl } from '../../reusable';
import { CheckIcon, InfoIcon } from '../../icons/Icons';
import { SliderIcon, AutoUpIcon } from './manage-icons';
import { IRSLimitDial } from './IRSLimitDial';

export function ContributionsCard({ contrib, pay, onAction, open, onToggle }) {
  const { t } = useTranslation();
  const [pretaxPct, setPretaxPct] = useState(contrib.pretaxPct);
  const [rothPct, setRothPct] = useState(contrib.rothPct);
  const [applied, setApplied] = useState(false);

  // Any edit clears a prior "applied" banner so it never lingers on a stale rate.
  useEffect(() => {
    setApplied(false);
  }, [pretaxPct, rothPct]);

  const gross = pay.grossPerCheck;
  const eePerCheck = (gross * (pretaxPct + rothPct)) / 100;
  const eeAnnual = eePerCheck * pay.perYear;
  const matchPct = Math.min(pretaxPct + rothPct, contrib.employerMatchPct);
  const matchAnnual = ((gross * matchPct) / 100) * pay.perYear;
  const preTaxDollar = (gross * pretaxPct) / 100;
  const rothDollar = (gross * rothPct) / 100;
  const takeHomeDrop = preTaxDollar * (1 - pay.federalEffective) + rothDollar;
  const totalAnnual = eeAnnual + matchAnnual;

  const checksPaid = pay.checksPaidYtd;
  const ytdAtRate =
    contrib.irsContributedYtd - ((contrib.pretaxPct - pretaxPct - rothPct) * gross * checksPaid) / 100;
  const projected = Math.round(ytdAtRate + eePerCheck * (pay.perYear - checksPaid));
  const irsProgress = Math.min(100, (projected / contrib.irsLimit) * 100);
  const willCap = projected >= contrib.irsLimit;
  const matchCaptured = matchPct >= contrib.employerMatchPct;

  // Current rates, from the same rows the Dashboard's card shows (active sources only,
  // label fallback, the derived "contributing with no election on file" row). A dollar
  // election is shown in dollars, never as the 0% its unused percent field carries.
  const rows = rateRows(contrib);
  // A source posting money with no elected rate carries value 0: only a known per-check
  // figure counts for it, as on the Dashboard's row.
  const perCheck = (r) => {
    if (r.activeNoRate) return r.perCheckDollar > 0 ? r.perCheckDollar : null;
    return r.mode === 'amount' ? r.value : r.perCheckDollar;
  };
  const showMatch = contrib.matchAvailability?.confidence !== 'unavailable' && contrib.employerMatchPct > 0;
  const perCheckKnown = rows.length > 0 && rows.every((r) => Number.isFinite(perCheck(r)));
  // The contract's total is employee dollars + employer match (INTEGRATION.md); use it when
  // sent, else rebuild it the same way from the rows and the match shown above it.
  const matchPerCheck = showMatch && Number.isFinite(contrib.employerMatchDollar) ? contrib.employerMatchDollar : 0;
  let totalPerCheck = null;
  if (Number.isFinite(contrib.totalPerCheck)) totalPerCheck = contrib.totalPerCheck;
  else if (perCheckKnown) totalPerCheck = rows.reduce((sum, r) => sum + perCheck(r), 0) + matchPerCheck;
  const autoKnown = typeof contrib.autoIncreaseOn === 'boolean';
  const autoState = contrib.autoIncreaseOn ? t('common.on') : t('common.off');
  const summary = [
    totalPerCheck != null && t('manage.contributions.summaryTotal', { amount: formatCurrency(totalPerCheck) }),
    autoKnown && t('manage.contributions.summaryAutoIncrease', { state: autoState }),
  ].filter(Boolean).join(' · ') || t('manage.contributions.noContribution');
  // The simulator multiplies by gross pay; without it every result would read $0.
  const canSimulate = gross > 0;

  const isChanged = pretaxPct !== contrib.pretaxPct || rothPct !== contrib.rothPct;
  const combined = pretaxPct + rothPct;
  const comboError = combined > contrib.planMaxPct;
  const canApply = isChanged && !comboError;

  return (
    <ProcessCard
      open={open}
      onToggle={onToggle}
      className="ma-contributions"
      tone="blue"
      icon={<SliderIcon />}
      title={t('manage.contributions.title')}
      question={t('manage.contributions.question')}
      ctaTone="blue"
      ctaLabel={t('manage.contributions.cta')}
      onCta={onAction}
      summary={summary}
    >
      <div className="ma-contrib-grid">
        {/* Current rates */}
        <div className="ma-panel">
          <div className="ma-panel__h">{t('manage.contributions.currentRates')}</div>
          <div className="ma-rate-stack">
            {rows.length === 0 && (
              <div className="ma-rate-row">
                <div className="ma-rate-l">
                  <span className="ma-rate-sub">{t('manage.contributions.noContribution')}</span>
                </div>
              </div>
            )}
            {rows.map((r) => {
              const isPct = r.mode === 'pct';
              const amount = perCheck(r);
              // The elected unit is the value; the other unit, when known, is the caption.
              let sub = null;
              if (r.activeNoRate) sub = t(r.pillKey);
              else if (isPct && Number.isFinite(r.perCheckDollar)) sub = `${formatCurrency(r.perCheckDollar)}${t('manage.lineup.perPaycheck')}`;
              else if (!isPct && r.pct != null) sub = t('dashboard.contributions.rateSecondaryPct', { pct: r.pct });
              return (
                <div className="ma-rate-row" key={r.source}>
                  <div className="ma-rate-l">
                    <span className="ma-rate-lbl">{r.label || t(r.labelKey, { defaultValue: r.source })}</span>
                    {sub && <span className="ma-rate-sub">{sub}</span>}
                  </div>
                  {isPct && (
                    <div className="ma-rate-bar">
                      <span style={{ width: `${r.value * 4}%` }} />
                    </div>
                  )}
                  <span className="ma-rate-val">
                    {isPct ? `${r.value}%` : Number.isFinite(amount) && `${formatCurrency(amount)}${t('manage.lineup.perPaycheck')}`}
                  </span>
                </div>
              );
            })}
            {showMatch && (
              <div className="ma-rate-row">
                <div className="ma-rate-l">
                  <span className="ma-rate-lbl">{t('manage.contributions.employerMatch')}</span>
                  <span className="ma-rate-sub">{t('manage.contributions.matchOnFirst', { pct: contrib.employerMatchPct })}</span>
                </div>
                <div className="ma-rate-bar ma-rate-bar--green">
                  <span style={{ width: `${contrib.employerMatchPct * 4}%` }} />
                </div>
                <span className="ma-rate-val ma-pos">+{contrib.employerMatchPct}%</span>
              </div>
            )}
          </div>
          {totalPerCheck != null && (
            <div className="ma-rate-total">
              <span className="ma-rate-total__lbl">{t('manage.contributions.totalPerPaycheck')}</span>
              <span className="ma-rate-total__val">{formatCurrency(totalPerCheck)}</span>
            </div>
          )}
          <div className="ma-meta-row">
            {autoKnown && (
              <div className="ma-meta">
                <span className="ma-meta__lbl">{t('manage.contributions.autoIncrease')}</span>
                <span className="ma-meta__val">
                  {contrib.autoIncreaseOn ? (
                    <span className="ma-on-pill">
                      <AutoUpIcon /> {autoState}
                    </span>
                  ) : autoState}
                  {contrib.autoIncreaseOn && contrib.autoIncreaseAmt != null && contrib.autoIncreaseNext
                    && t('manage.contributions.autoIncreaseNext', { amt: contrib.autoIncreaseAmt, date: contrib.autoIncreaseNext })}
                </span>
              </div>
            )}
            {(contrib.lastChange || contrib.lastChangeDate) && (
              <div className="ma-meta">
                <span className="ma-meta__lbl">{t('manage.contributions.lastChange')}</span>
                <span className="ma-meta__val">
                  {[contrib.lastChange, contrib.lastChangeDate].filter(Boolean).join(' · ')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 2026 IRS limit dial */}
        <div className="ma-panel ma-irs-panel">
          <div className="ma-panel__h">{t('manage.contributions.irsLimit')}</div>
          <div className="ma-irs-dial">
            <IRSLimitDial
              contributedYtd={contrib.irsContributedYtd}
              projected={isChanged ? projected : null}
              limit={contrib.irsLimit}
              willCap={willCap}
            />
          </div>
          <div className="ma-irs-figures">
            <div className="ma-irs-row">
              <span className="ma-dotmk ma-dotmk--blue" aria-hidden="true" />
              <span className="ma-irs-lbl">{t('manage.contributions.contributedYtd')}</span>
              <span className="ma-irs-val">{formatCurrency(contrib.irsContributedYtd, false)}</span>
            </div>
            {isChanged && (
              <div className="ma-irs-row">
                <span
                  className={cx('ma-dotmk', 'ma-dotmk--dashed', willCap ? 'ma-dotmk--purple' : 'ma-dotmk--blue')}
                  aria-hidden="true"
                />
                <span className="ma-irs-lbl">{t('manage.contributions.projectedAt', { pct: combined })}</span>
                <span className="ma-irs-val">{formatCurrency(projected, false)}</span>
              </div>
            )}
            <div className="ma-irs-row">
              <span className="ma-dotmk ma-dotmk--muted" aria-hidden="true" />
              <span className="ma-irs-lbl">{t('manage.contributions.aggregateLimit')}</span>
              <span className="ma-irs-val">{formatCurrency(contrib.irsLimit, false)}</span>
            </div>
          </div>
        </div>

        {/* Simulator */}
        {canSimulate && (
        <div className="ma-panel ma-sim-panel">
          <div className="ma-panel__h ma-panel__h--split">
            <span>{t('manage.contributions.adjustRate')}</span>
            <span className="ma-sim-tag">{t('manage.contributions.editable')}</span>
          </div>
          <div className="ma-sim-sliders">
            <RateControl
              label={t('manage.contributions.preTax')}
              value={pretaxPct}
              onChange={setPretaxPct}
              max={contrib.planMaxPct}
              accent="blue"
              invalid={comboError}
              hint={
                pretaxPct === contrib.pretaxPct
                  ? t('manage.contributions.currentRate')
                  : t('manage.contributions.fromCurrent', {
                    delta: `${pretaxPct > contrib.pretaxPct ? '+' : ''}${pretaxPct - contrib.pretaxPct}`,
                  })
              }
            />
            <RateControl
              label={t('manage.contributions.roth')}
              value={rothPct}
              onChange={setRothPct}
              max={contrib.planMaxPct}
              accent="purple"
              invalid={comboError}
              hint={rothPct === 0 ? t('manage.contributions.currentlyZero') : t('manage.contributions.rothAdd', { pct: rothPct })}
            />
          </div>

          <div className="ma-sim-results">
            <div className="ma-sim-result">
              <span className="ma-sim-r__lbl">{t('manage.contributions.takeHome')}</span>
              <span className="ma-sim-r__val ma-neg">−{formatCurrency(takeHomeDrop)}</span>
              <span className="ma-sim-r__sub">{t('manage.contributions.takeHomeSub')}</span>
            </div>
            <div className="ma-sim-result">
              <span className="ma-sim-r__lbl">{t('manage.contributions.annualContribution')}</span>
              <span className="ma-sim-r__val">{formatCurrency(eeAnnual, false)}</span>
              <span className="ma-sim-r__sub">{t('manage.contributions.perCheckCalc', { perCheck: formatCurrency(eePerCheck), count: pay.perYear })}</span>
            </div>
            <div className="ma-sim-result">
              <span className="ma-sim-r__lbl">{t('manage.contributions.matchCapturedLabel')}</span>
              <span className={cx('ma-sim-r__val', matchCaptured ? 'ma-pos' : 'ma-warn')}>
                {formatCurrency(matchAnnual, false)}
                {matchCaptured && <CheckIcon width={13} height={13} />}
              </span>
              <span className="ma-sim-r__sub">
                {matchCaptured
                  ? t('manage.contributions.fullMatch')
                  : t('manage.contributions.bumpToCapture', { pct: contrib.employerMatchPct })}
              </span>
            </div>
            <div className="ma-sim-result">
              <span className="ma-sim-r__lbl">{t('manage.contributions.totalToRetirement')}</span>
              <span className="ma-sim-r__val">{formatCurrency(totalAnnual, false)}</span>
              <span className="ma-sim-r__sub">{t('manage.contributions.totalToRetirementSub')}</span>
            </div>
          </div>

          {comboError && (
            <div className="ma-sim-callout ma-sim-callout--error" role="alert">
              <span className="ma-callout-ic" aria-hidden="true">!</span>
              <span>{t('manage.contributions.comboError', { max: contrib.planMaxPct, combined })}</span>
            </div>
          )}
          {!comboError && isChanged && !applied && (
            <div className={cx('ma-sim-callout', willCap ? 'ma-sim-callout--warn' : 'ma-sim-callout--info')}>
              <InfoIcon width={16} height={16} />
              {willCap ? (
                <span>{t('manage.contributions.willCapInfo', { combined, limit: formatCurrency(contrib.irsLimit, false) })}</span>
              ) : (
                <span>
                  {t('manage.contributions.projectedInfo', {
                    projected: formatCurrency(projected, false),
                    limit: formatCurrency(contrib.irsLimit, false),
                    pct: Math.round(irsProgress),
                  })}
                </span>
              )}
            </div>
          )}
          {applied && !comboError && (
            <div className="ma-sim-callout ma-sim-callout--success" role="status">
              <CheckIcon width={16} height={16} />
              <span>
                {t('manage.contributions.appliedInfo', {
                  pretax: pretaxPct,
                  roth: rothPct > 0 ? ` · Roth ${rothPct}%` : '',
                })}
              </span>
            </div>
          )}

          <div className="ma-sim-apply">
            <span className="ma-sim-apply__meta">
              {comboError
                ? t('manage.contributions.applyFix')
                : isChanged
                  ? t('manage.contributions.applyNew', { combined })
                  : t('manage.contributions.applyAdjust')}
            </span>
            <button
              type="button"
              className="ma-apply-btn"
              disabled={!canApply}
              aria-disabled={!canApply}
              onClick={() => setApplied(true)}
            >
              {applied && !comboError ? (
                <>
                  <CheckIcon width={14} height={14} /> {t('manage.contributions.applied')}
                </>
              ) : (
                t('manage.contributions.applyBtn')
              )}
            </button>
          </div>
        </div>
        )}
      </div>
    </ProcessCard>
  );
}

ContributionsCard.propTypes = {
  contrib: PropTypes.object.isRequired,
  pay: PropTypes.object.isRequired,
  onAction: PropTypes.func,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default ContributionsCard;
